/**
 * Damage routing and calculation utilities.
 *
 * This module handles calculating damage direction based on attack angle
 * and provides types for damage flow through mech parts.
 */

/**
 * Direction from which damage is received.
 *
 * This determines which parts take damage first:
 * - front: Weapon → Core → Head
 * - rear: Transport → Core → Head
 * - area: Weapon → Transport → Core → Head (distributed damage)
 */
export type DamageDirection = 'front' | 'rear' | 'area';

/**
 * Calculate damage direction based on hit angle relative to mech facing.
 *
 * The calculation determines if the hit is from the front or rear:
 * - Front = within 90° of facing direction
 * - Rear = within 90° of opposite facing direction
 *
 * @param mechPosition - Position of the mech being hit
 * @param mechRotation - Current rotation of the mech in radians
 * @param projectilePosition - Position of the incoming projectile
 * @returns The damage direction ('front' or 'rear')
 */
export function getDamageDirection(
  mechPosition: { x: number; y: number },
  mechRotation: number,
  projectilePosition: { x: number; y: number }
): DamageDirection {
  // Calculate angle from mech to projectile
  const dx = projectilePosition.x - mechPosition.x;
  const dy = projectilePosition.y - mechPosition.y;
  const angleToProjectile = Math.atan2(dy, dx);

  // Normalize mech rotation to match atan2 output
  // mechRotation is already in radians but offset by -π/2 (from handleAiming)
  // We need to add π/2 to get the actual facing direction
  const mechFacing = mechRotation + Math.PI / 2;

  // Calculate relative angle (difference between projectile angle and facing)
  let relativeAngle = angleToProjectile - mechFacing;

  // Normalize to range [-π, π]
  while (relativeAngle > Math.PI) relativeAngle -= 2 * Math.PI;
  while (relativeAngle < -Math.PI) relativeAngle += 2 * Math.PI;

  // Determine direction based on relative angle
  // Front: -90° to +90° (-π/2 to +π/2)
  // Rear: outside that range
  if (Math.abs(relativeAngle) <= Math.PI / 2) {
    return 'front';
  } else {
    return 'rear';
  }
}
